/**
 * Показ данных в кабинете: клеймо, полосы, индекс, ряд во времени, матрица.
 *
 * Всё здесь — буквальное исполнение reference/data-viz.md, и три его правила
 * важнее удобства:
 *
 * 1. **Ширина полосы живёт в разметке** как `--w`, а не в скрипте. Иначе
 *    краулер, печать в PDF и человек с выключенной анимацией получают пустой
 *    график. Знаменатель — максимум **в текущей выборке**, и он не меняется от
 *    сортировки и страницы: короткие полосы на второй странице — это правда, а
 *    не дефект.
 * 2. **Число рядом с полосой обязательно.** Серая заливка даёт 1,34:1 к своей
 *    дорожке, то есть сама по себе графиком не является.
 * 3. **«Не измерено» — это слова**, штриховка и нулевая заливка. Никогда не
 *    ноль, никогда пустая ячейка, никогда `n/a`.
 *
 * Провенанс: данные клиента несут `.stamp` и **ни одного значка**. Значок
 * `.badge.pub` означает публикацию и на своих числах — ошибка (data-viz.md §1).
 */

import {
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { t } from '../i18n';
import { tx } from '../i18n/react';
import {
  dateNumeric,
  dateShort,
  decimal,
  int,
  percent,
  score as scoreFmt,
  statusLadder,
} from '@shared/src/format';
import { barGeometries } from '@shared/src/bars';

/* ────────────────────────────── основание ─────────────────────────────────── */

/**
 * Клеймо: что измерено, сколько движков, за какой период, насколько полно.
 * Правило экранное, а не поштучное: таблица из четырёхсот ячеек не подписывает
 * каждую, но экран без основания не выпускается.
 */
export function Stamp({ children }: { children: ReactNode }) {
  return <p className="stamp basis">{children}</p>;
}

/** График — всегда `<figure>` с подписью, где стоит основание. */
export function Figure({
  caption,
  children,
  head,
}: {
  /** Подпись необязательна: основание экрана может стоять один раз наверху. */
  caption?: ReactNode;
  children: ReactNode;
  head?: ReactNode;
}) {
  return (
    <figure className="fig">
      {head}
      {children}
      {caption ? <figcaption className="stamp">{caption}</figcaption> : null}
    </figure>
  );
}

/* ──────────────────────────────── дельты ──────────────────────────────────── */

/**
 * Пилюля движения: знак, число и слово. Стрелочных глифов в системе нет —
 * `▲ ▼` приходят из системного запасного шрифта и сидят мимо базовой линии.
 */
export function Pill({
  value,
  text,
  /** У средней позиции уменьшение — улучшение, поэтому направление задаётся снаружи. */
  goodWhenNegative = false,
}: {
  value: number | null | undefined;
  text: string;
  goodWhenNegative?: boolean;
}) {
  if (value === null || value === undefined) {
    return <span className="pill flat">{text}</span>;
  }
  const better = goodWhenNegative ? value < 0 : value > 0;
  const worse = goodWhenNegative ? value > 0 : value < 0;
  const kind = value === 0 ? 'flat' : better ? 'up' : worse ? 'down' : 'flat';
  return <span className={`pill ${kind}`}>{text}</span>;
}

/* ───────────────────────────────── полосы ─────────────────────────────────── */

export interface BarItem {
  key: string;
  name: string;
  /** Вторая строка под именем: движок, домен, что угодно моноширинное. */
  note?: string;
  /** `null` — не измерено. Ноль и «не измерено» — разные вещи. */
  value: number | null;
  /** Готовая строка значения. Без неё берётся процент. */
  display?: string;
  /** Своя строка клиента: подчёркивается чернилами, а не акцентом. */
  isOwn?: boolean;
}

/**
 * Горизонтальные полосы одной выборки.
 *
 * Акцент достаётся лидеру, даже если лидер — конкурент: покрасить свою полосу
 * акцентом значит превратить измерение в буклет. Своя строка находится иначе —
 * чернильным ребром слева, полужирным именем и моношрифтовой меткой «Вы».
 */
export function Bars({ items }: { items: BarItem[] }) {
  // Геометрия — общей функцией, той же, которой считает витрина. Формула тут
  // была своя и правильная, а на лендинге — своя и абсолютная, из-за чего одни
  // и те же данные рисовались двумя разными способами на двух поверхностях
  // одного продукта. Правило одной формулы (спека §8) относится и к ширинам.
  const geometry = barGeometries(items);

  return (
    <div className="bars">
      {items.map((item, i) => {
        const { width, lead, measured } = geometry[i]!;
        return (
          <div
            className={`brow${measured ? '' : ' na'}${item.isOwn ? ' own' : ''}`}
            key={item.key}
          >
            <div className="brow-name">
              {item.name}
              {item.isOwn ? <em className="you">Вы</em> : null}
              {item.note ? <span>{item.note}</span> : null}
            </div>
            <div className="brow-track">
              <div
                className={`brow-fill${lead ? ' top' : ''}`}
                style={{ '--w': width } as CSSProperties}
              />
            </div>
            <div className="brow-val">
              {measured ? (item.display ?? percent(item.value!, 1)) : t('not-measured')}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─────────────────────────── индекс ИИ-видимости ──────────────────────────── */

/**
 * Индекс — это число, а не цвет (data-viz.md §2). Балл, слово статуса рядом,
 * под ними дорожка с засечками на 20/40/60/80. Семантические цвета к индексу не
 * применяются: они означают событие, а не уровень.
 */
export function IndexGauge({
  value,
  deltaText,
  deltaValue,
}: {
  value: number | null | undefined;
  deltaText?: string;
  deltaValue?: number | null;
}) {
  const has = value !== null && value !== undefined;
  const width = has ? Math.max(0, Math.min(100, value)) : 0;
  const status = has ? statusLadder(value) : t('not-measured');
  const low = has && value < 40;

  return (
    <div className="gauge">
      <div className="gauge-head">
        <span className="value tnum">{has ? scoreFmt(value) : '—'}</span>
        <span className={`gauge-status${low ? ' low' : ''}`}>{status}</span>
        {deltaText ? (
          <Pill value={deltaValue} text={deltaText} />
        ) : (
          <span className="pill flat">{t('viz.no-compare')}</span>
        )}
      </div>
      <div className="gauge-track" aria-hidden="true">
        <i style={{ '--w': `${width}%` } as CSSProperties} />
        {[20, 40, 60, 80].map((tick) => (
          <b className="gauge-tick" key={tick} style={{ left: `${tick}%` }} />
        ))}
      </div>
      <p className="gauge-scale" aria-hidden="true">
        {[0, 20, 40, 60, 80, 100].map((tick) => (
          <span key={tick}>{int(tick)}</span>
        ))}
      </p>
    </div>
  );
}

/* ────────────────────────────── ряд во времени ────────────────────────────── */

export interface SeriesPoint {
  /** Московская дата снимка. */
  date: string;
  /** `null` — разрыв. Через ноль линия не проводится никогда. */
  value: number | null;
  /** Незавершённый цикл: этот отрезок рисуется пунктиром и только он. */
  incomplete?: boolean;
}

export interface Series {
  key: string;
  name: string;
  points: SeriesPoint[];
  /** Предмет замера: 2px акцентом. Остальные — 1,5px чернилами. */
  subject?: boolean;
}

export interface TrendEvent {
  /**
   * Своя строка события. Ключом идёт именно она, а не дата с видом: за один день
   * событий одного вида может быть несколько (две правки сайта, два движка), и
   * на совпавших ключах React переставил бы отметке чужой заголовок.
   */
  id: string;
  date: string;
  /** Вид границы одним словом: он стоит подписью у вертикали. */
  label: string;
  /**
   * Заголовок события из данных. Подпись говорит, ЧТО за граница; заголовок —
   * что именно случилось («Gemini, DeepSeek и Qwen начали отвечать с
   * веб-поиском»), и в подпись он не влезает. Уезжает в `<title>` отметки:
   * подсказка мышью и доступное имя для чтения с экрана.
   */
  note?: string;
}

const PLOT = { w: 960, h: 280, left: 52, right: 168, top: 22, bottom: 24 };

/**
 * Разбиение ряда на отрезки.
 *
 * Два правила сразу. **Разрыв рвёт линию**: точка без значения заканчивает
 * отрезок, и через ноль линия не проводится. **Пунктиром идёт только
 * незавершённый цикл**, а не весь ряд, в котором такая точка нашлась: иначе
 * один неполный день перечёркивает месяц настоящих измерений. Граничная точка
 * попадает в оба отрезка — линия остаётся непрерывной.
 */
function segmentsOf(points: SeriesPoint[]): { points: SeriesPoint[]; dashed: boolean }[] {
  const parts: { points: SeriesPoint[]; dashed: boolean }[] = [];
  let previous: SeriesPoint | null = null;

  for (const point of points) {
    if (point.value === null) {
      previous = null;
      continue;
    }
    const dashed = Boolean(point.incomplete);
    const current = parts.at(-1);
    if (previous && current && current.dashed === dashed) {
      current.points.push(point);
    } else if (previous) {
      parts.push({ points: [previous, point], dashed });
    } else {
      parts.push({ points: [point], dashed });
    }
    previous = point;
  }

  return parts.filter((part) => part.points.length > 1 || parts.length === 1);
}

/**
 * Разводит подписи концов линий по высоте, сохраняя их порядок. Сдвигается
 * только подпись — точка и линия остаются там, где им положено данными.
 */
function spreadLabels(
  ends: { key: string; y: number | null }[],
  gap = 15,
): Map<string, number> {
  const known = ends
    .filter((end): end is { key: string; y: number } => end.y !== null)
    .sort((a, b) => a.y - b.y);

  const out = new Map<string, number>();
  let previous = -Infinity;
  for (const end of known) {
    const at = Math.max(end.y, previous + gap);
    out.set(end.key, at);
    previous = at;
  }
  return out;
}

/** Верх шкалы — ближайшее круглое число над максимумом. Низ всегда ноль. */
function niceTop(values: number[], fixed?: number): number {
  if (fixed) return fixed;
  const max = values.length ? Math.max(...values) : 0;
  if (max <= 0) return 10;
  const step = max > 50 ? 20 : max > 20 ? 10 : max > 10 ? 5 : 2;
  return Math.ceil(max / step) * step;
}

/**
 * Линии во времени.
 *
 * Ось Y всегда от нуля — усечённая шкала в измерительном продукте это подлог.
 * Конкуренты различаются подписью на конце линии, а не цветом и не пунктиром:
 * пунктир в этой системе означает ровно одно — незавершённый цикл. Внешние
 * события стоят вертикальными волосяными линиями с моношрифтовой подписью.
 *
 * Текстовый эквивалент обязателен (data-viz.md §9) и лежит под переключателем
 * «Показать данные»: ширина линии сама по себе не кодирует ничего для того, кто
 * график не видит.
 */
export function Trend({
  series,
  events = [],
  unit = '%',
  fixedTop,
  caption,
  emptyText,
}: {
  series: Series[];
  events?: TrendEvent[];
  unit?: string;
  /** Индекс всегда рисуется в шкале 0–100, что бы ни лежало в данных. */
  fixedTop?: number;
  caption?: ReactNode;
  emptyText?: string;
}) {
  const titleId = useId();
  const dates = [...new Set(series.flatMap((s) => s.points.map((p) => p.date)))].sort();
  const all = series.flatMap((s) =>
    s.points.map((p) => p.value).filter((v): v is number => v !== null),
  );
  const top = niceTop(all, fixedTop);

  // Положение по времени, а не по номеру точки. Иначе внешнее событие, которое
  // случилось между двумя точками ряда, встаёт не туда — а именно им и
  // объясняют, почему цифра сдвинулась.
  const times = dates.map((date) => Date.parse(date));
  const tMin = Math.min(...times);
  const tMax = Math.max(...times);
  const x = (date: string) => {
    const span = PLOT.w - PLOT.left - PLOT.right;
    if (tMax === tMin) return PLOT.left;
    const at = (Date.parse(date) - tMin) / (tMax - tMin);
    return PLOT.left + Math.max(0, Math.min(1, at)) * span;
  };
  const y = (value: number) => {
    const span = PLOT.h - PLOT.top - PLOT.bottom;
    return PLOT.top + span - (value / top) * span;
  };

  if (dates.length === 0 || all.length === 0) {
    return (
      <Figure caption={caption}>
        <div className="chart empty">
          <p className="stamp">{emptyText ?? t('viz.empty')}</p>
        </div>
      </Figure>
    );
  }

  const grid = [0, 0.25, 0.5, 0.75, 1].map((k) => k * top);

  // Подписи на концах линий — единственный способ отличить конкурентов друг от
  // друга (цветом их не кодируют). Две близкие линии дали бы две наложенные
  // подписи, то есть ни одной читаемой, поэтому подписи разводятся по высоте.
  const labels = spreadLabels(
    series.map((s) => {
      const last = [...s.points].reverse().find((p) => p.value !== null);
      return { key: s.key, y: last ? y(last.value as number) : null };
    }),
  );

  return (
    <Figure caption={caption}>
      <div className="chart">
        <svg
          viewBox={`0 0 ${PLOT.w} ${PLOT.h}`}
          role="img"
          aria-labelledby={titleId}
          preserveAspectRatio="none"
          className="tr"
        >
          <title id={titleId}>
            {t('viz.series-title', { names: series.map((s) => s.name).join(', ') })}
          </title>

          {grid.map((value) => (
            <g key={value}>
              <line
                className="tr-grid"
                x1={PLOT.left}
                x2={PLOT.w - PLOT.right}
                y1={y(value)}
                y2={y(value)}
              />
              <text
                className="tr-axis"
                textAnchor="end"
                x={PLOT.left - 10}
                y={y(value) + 4}
              >
                {decimal(value, 0)}
                {unit}
              </text>
            </g>
          ))}

          {/* Внешние события: волосяная вертикаль с моношрифтовой подписью.
              Подписи чередуются по высоте — два события в соседних сутках иначе
              наезжают друг на друга и не читаются оба. */}
          {events.map((event, i) => (
            <g key={event.id}>
              {event.note ? <title>{`${event.note} · ${event.date}`}</title> : null}
              <line
                className="tr-event"
                x1={x(event.date)}
                x2={x(event.date)}
                y1={PLOT.top - 10}
                y2={PLOT.h - PLOT.bottom}
              />
              <text
                className="tr-event-label"
                x={x(event.date) + 4}
                y={PLOT.top - (i % 2 === 0 ? 10 : 0)}
              >
                {event.label}
              </text>
            </g>
          ))}

          {series.map((s) => {
            const parts = segmentsOf(s.points);
            const last = [...s.points].reverse().find((p) => p.value !== null);
            const labelY = labels.get(s.key);

            return (
              <g key={s.key} className={s.subject ? 'tr-subject' : 'tr-rival'}>
                {parts.map((part, i) => {
                  const path = part.points
                    .map(
                      (p, j) =>
                        `${j === 0 ? 'M' : 'L'}${x(p.date)},${y(p.value as number)}`,
                    )
                    .join(' ');
                  return (
                    <path
                      key={`${s.key}-${i}`}
                      d={path}
                      className={`tr-line${part.dashed ? ' tr-dashed' : ''}`}
                    />
                  );
                })}
                {last ? (
                  <>
                    <circle
                      className="tr-dot"
                      cx={x(last.date)}
                      cy={y(last.value as number)}
                      r={3}
                    />
                    <text
                      className="tr-end"
                      x={PLOT.w - PLOT.right + 10}
                      y={(labelY ?? y(last.value as number)) + 4}
                    >
                      {s.name} {decimal(last.value as number, 1)}
                      {unit}
                    </text>
                  </>
                ) : null}
              </g>
            );
          })}
        </svg>
      </div>

      <details className="chart-data">
        <summary>{t('viz.show-data')}</summary>
        <div className="tbl-wrap">
          <table className="mini">
            {/* Текстовый эквивалент остаётся названным, даже когда подпись у
                графика снята: таблица без заголовка для читателя с экрана —
                просто набор чисел. */}
            <caption>
              {caption ??
                t('viz.series-title', { names: series.map((s) => s.name).join(', ') })}
            </caption>
            <thead>
              <tr>
                <th scope="col">{t('viz.col.date')}</th>
                {series.map((s) => (
                  <th scope="col" key={s.key}>
                    {s.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dates.map((date) => (
                <tr key={date}>
                  <td className="tnum">{date}</td>
                  {series.map((s) => {
                    const point = s.points.find((p) => p.date === date);
                    return (
                      <td className="tnum num" key={s.key}>
                        {point && point.value !== null
                          ? `${decimal(point.value, 1)}${unit}`
                          : t('not-measured')}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Отметки — часть текстового эквивалента, а не украшение линии: именно
            ими объясняется перелом ряда, и читателю с экрана они нужны ровно
            затем же, зачем зрячему. В самом SVG их `<title>` не звучит — график
            объявлен одной картинкой с именем от корневого `<title>`, и подсказка
            вложенной группы остаётся мышиной. */}
        {events.length > 0 ? (
          <div className="tbl-wrap">
            <table className="mini">
              <caption>
                {t('viz.marks-prefix')}{' '}
                {caption ?? <>{series.map((s) => s.name).join(', ')}</>}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{t('viz.col.date')}</th>
                  <th scope="col">{t('viz.col.what')}</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id}>
                    <td className="tnum">{event.date}</td>
                    <td>{event.note ?? event.label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </details>
    </Figure>
  );
}

/** Сколько подписей значений помещается в узкой ячейке, не наезжая друг на друга. */
const MULT_LABELS = 6;

/**
 * Какие точки ряда подписываются числом и датой.
 *
 * Подписан каждый замер, пока их немного; в длинном ряду подписи выбираются
 * равномерно, но края берутся всегда — первый и последний замер это границы
 * периода, названного над сеткой. Молча подписать только часть точек нельзя,
 * поэтому невыбранные остаются точками на линии, а их числа стоят в таблице
 * под «Показать данные».
 */
function labelled(count: number, keep = MULT_LABELS): Set<number> {
  if (count <= keep) return new Set(Array.from({ length: count }, (_, i) => i));
  const out = new Set<number>();
  for (let k = 0; k < keep; k += 1) out.add(Math.round((k * (count - 1)) / (keep - 1)));
  return out;
}

/**
 * Малые кратные для сравнения движков: одна ячейка на движок, общая шкала,
 * подпись и число у каждой. Девяти цветов у нас нет и не будет — движки
 * никогда не кодируются цветом (data-viz.md §5).
 *
 * Шкала общая и **названа один раз** над сеткой (data-viz.md §5) вместе с
 * границами периода: без верха шкалы одинаковый на глаз излом у 39% и у 1%
 * читается как одинаковое движение, а линия без единой подписи сообщает только
 * «что-то менялось».
 *
 * **Число стоит на своей точке, а дата — под ней.** Прежде ряд шёл строкой
 * чисел под линией, и это было перечисление, а не ось: место числа в строке
 * означало дату лишь на словах, а глазом число со своим изломом не связывалось
 * вовсе. Теперь у каждого подписанного замера своя вертикаль — число над
 * точкой, дата под осью, — и подпись читается вместе с линией.
 * design: derived, not documented — data-viz.md §4 оставляет точку только
 * последнему замеру, но то правило написано для большого ряда с осью Y и
 * наведением; в ячейке размером с ладонь ни того, ни другого нет, и без точки с
 * числом ячейка снова превращается в спарклайн.
 *
 * Пропуск в ряду — штриховка во всю высоту ячейки, та же, что у полосы «Не
 * измерено» (data-viz.md §3.5): линия через него не проводится, и нулём он не
 * притворяется.
 */
export function SmallMultiples({
  cells,
  unit = '%',
  caption,
}: {
  cells: { key: string; name: string; points: SeriesPoint[]; value: number | null }[];
  unit?: string;
  caption?: ReactNode;
}) {
  const all = cells.flatMap((c) =>
    c.points.map((p) => p.value).filter((v): v is number => v !== null),
  );
  const top = niceTop(all);

  // Даты ряда — общие на всю сетку: ими подписан период под шкалой, по ним
  // строится текстовый эквивалент и по ним же считается место точки по
  // горизонтали. Считать место по НОМЕРУ измеренной точки внутри ячейки нельзя:
  // движок с пропущенной датой растянул бы оставшиеся точки на всю ширину, и
  // одна и та же вертикаль в соседних ячейках перестала бы означать одну дату —
  // то есть малые кратные перестали бы сравниваться, ради чего они и стоят.
  const dates = [...new Set(cells.flatMap((c) => c.points.map((p) => p.date)))].sort();
  const measured = cells.some((c) => c.points.some((p) => p.value !== null));
  const at = new Map(dates.map((date, i) => [date, i]));
  const marks = labelled(dates.length);

  // Место в процентах ширины и высоты площадки: разметка ячейки тянется вместе
  // с колонкой, и проценты тянутся с ней сами. Числа при этом остаются текстом
  // разметки, а не текстом внутри растянутого SVG, где они поехали бы по
  // ширине вслед за ячейкой.
  const x = (date: string) => {
    const i = at.get(date);
    if (i === undefined) return 50;
    return dates.length <= 1 ? 50 : (i / (dates.length - 1)) * 100;
  };
  const y = (value: number) => 100 - (value / top) * 100;
  // Подпись у края прижимается к своей точке, а не центрируется по ней: иначе
  // половина крайнего числа уезжает за карточку.
  const side = (date: string) =>
    at.get(date) === 0 ? ' at-start' : at.get(date) === dates.length - 1 ? ' at-end' : '';

  return (
    <Figure
      caption={caption}
      head={
        measured ? (
          <p className="stamp">
            {tx('viz.mult.head', {
              top: decimal(top, 0),
              unit,
              points: dates.length,
              from: dateNumeric(dates[0]!),
              to: dateNumeric(dates.at(-1)!),
              tnum: (chunks) => <span className="tnum">{chunks}</span>,
            })}
          </p>
        ) : null
      }
    >
      <div className="multiples">
        {cells.map((cell) => {
          // Ряд ячейки достраивается до ОБЩЕЙ сетки дат. Дата, которой у движка
          // нет вовсе, и дата без значения — для читателя одно и то же: замера
          // не было. Без этого пропуск исчезал из ячейки совсем, линия шла
          // сквозь него плавно, а таблица под «Показать данные» на ту же дату
          // печатала «Не измерено» — два разных ответа об одних данных.
          const known = new Map(cell.points.map((p) => [p.date, p]));
          const row: SeriesPoint[] = dates.map(
            (date) => known.get(date) ?? { date, value: null },
          );
          const points = row.filter((p) => p.value !== null);
          // Разрыв рвёт линию и здесь, тем же разбиением, что у большого ряда:
          // соединив соседние измеренные точки через «Не измерено», ячейка
          // показывала бы плавное изменение там, где замера не было вовсе.
          const parts = segmentsOf(row);

          return (
            <div className="mult" key={cell.key}>
              <div className="mult-head">
                <span className="mult-name">{cell.name}</span>
                <span className="mult-val tnum">
                  {cell.value === null
                    ? t('not-measured')
                    : `${decimal(cell.value, 1)}${unit}`}
                </span>
              </div>
              {dates.length > 0 ? (
                <>
                  {/* Площадка стоит и у пустой ячейки: движок, не измерявшийся
                      ни разу, остаётся в общей шкале дат штриховкой на каждой
                      дате, а не выпадает из сетки одной фразой. Малые кратные
                      сравниваются местом на площадке, и ячейка вдвое ниже
                      соседних сравнивать нечем.
                      Читателю с экрана площадка не нужна: её содержание слово в
                      слово стоит ниже отдельным абзацем и в таблице под
                      «Показать данные», а озвученный трижды ряд — это не три
                      источника, а один и тот же, прочитанный трижды. */}
                  <div className="mult-plot" aria-hidden="true">
                    {/* Пропуск виден на своём месте, а не выпадает из ряда:
                        штриховка стоит той же датой, что и остальные точки, и
                        лежит ПОД линией — иначе она срезала бы концы соседних
                        отрезков. */}
                    {row.map((p) =>
                      p.value === null ? (
                        <span
                          key={`gap-${p.date}`}
                          className="mult-gap na-hatch"
                          style={{ left: `${x(p.date)}%` }}
                          title={`${dateNumeric(p.date)} — ${t('not-measured.inline')}`}
                        />
                      ) : null,
                    )}

                    <svg
                      viewBox="0 0 100 100"
                      className="mult-line"
                      preserveAspectRatio="none"
                    >
                      {/* Волосяные горизонтали и ноль внизу: ось Y всегда от
                          нуля, усечённая шкала в измерительном продукте это
                          подлог (data-viz.md §4). Вертикальной сетки нет. */}
                      {[0, 50, 100].map((rule) => (
                        <line
                          key={rule}
                          className={rule === 100 ? 'mult-base' : 'mult-rule'}
                          x1={0}
                          x2={100}
                          y1={rule}
                          y2={rule}
                          vectorEffect="non-scaling-stroke"
                        />
                      ))}
                      {parts.map((part, i) => (
                        /* Ячейка растянута по ширине (`preserveAspectRatio:
                           none`), и вместе с ней растягивалась бы толщина
                           линии: по горизонтали жирнее, по вертикали тоньше
                           положенных 1,5px. Пунктир, как и у большого ряда,
                           означает ровно одно — незавершённый цикл. */
                        <path
                          key={`${cell.key}-${i}`}
                          d={part.points
                            .map(
                              (p, j) =>
                                `${j === 0 ? 'M' : 'L'}${x(p.date).toFixed(2)},${y(
                                  p.value as number,
                                ).toFixed(2)}`,
                            )
                            .join(' ')}
                          className={`tr-line${part.dashed ? ' tr-dashed' : ''}`}
                          vectorEffect="non-scaling-stroke"
                        />
                      ))}
                    </svg>

                    {/* Точка на каждом замере. Незавершённый цикл остаётся
                        полым кружком: он и на линии пунктир, и в таблице
                        отмечен. */}
                    {row.map((p) =>
                      p.value === null ? null : (
                        <span
                          key={`dot-${p.date}`}
                          className={`mult-dot${p.incomplete ? ' is-open' : ''}`}
                          style={{ left: `${x(p.date)}%`, top: `${y(p.value)}%` }}
                        />
                      ),
                    )}

                    {/* Число стоит над своей точкой. Сторона у всей ячейки одна:
                        разложенные по правилу «выше или ниже точки» подписи
                        одного ряда читались как ещё одно свойство замера,
                        которого у него нет. Место сверху под них отведено
                        полем площадки, поэтому в заголовок они не заходят. */}
                    {row.map((p) =>
                      p.value === null || !marks.has(at.get(p.date)!) ? null : (
                        <span
                          key={`tag-${p.date}`}
                          className={`mult-tag tnum${side(p.date)}`}
                          style={{ left: `${x(p.date)}%`, top: `${y(p.value)}%` }}
                        >
                          {decimal(p.value, 1)}
                          {unit}
                        </span>
                      ),
                    )}
                  </div>

                  {/* Ось дат: у подписанного числа своя дата ровно под ним. */}
                  <div className="mult-axis">
                    {row.map((p) =>
                      marks.has(at.get(p.date)!) ? (
                        <span
                          key={`date-${p.date}`}
                          className={`mult-date tnum${side(p.date)}`}
                          style={{ left: `${x(p.date)}%` }}
                        >
                          {dateShort(p.date)}
                        </span>
                      ) : null,
                    )}
                  </div>

                  {/* Тот же ряд словами — для чтения с экрана: подписи на
                      графике идут без дат, а таблица с датами лежит под
                      закрытым переключателем на всю сетку сразу. */}
                  <p className="visually-hidden">
                    {cell.name}:{' '}
                    {row
                      .map(
                        (p) =>
                          `${dateNumeric(p.date)} — ${
                            p.value === null
                              ? t('not-measured.inline')
                              : `${decimal(p.value, 1)}${unit}`
                          }`,
                      )
                      .join('; ')}
                  </p>

                  {/* Ноль замеров и один замер — разные состояния, и общая
                      подпись на оба врала про ту ячейку, где замера не было ни
                      разу. Единственный замер показывается как замер — точкой и
                      числом, — и объясняется словами: линии нет не потому, что
                      что-то сломалось, а потому, что соединять пока нечего. */}
                  {points.length === 0 ? (
                    <p className="stamp">{t('viz.mult.none')}</p>
                  ) : points.length === 1 ? (
                    <p className="stamp">{t('viz.mult.single')}</p>
                  ) : null}
                </>
              ) : (
                <p className="stamp">{t('viz.mult.none')}</p>
              )}
            </div>
          );
        })}
      </div>

      {measured ? (
        <details className="chart-data">
          <summary>{t('viz.show-data')}</summary>
          <div className="tbl-wrap">
            <table className="mini">
              <caption>
                {caption ??
                  t('viz.mult.by-engines', {
                    names: cells.map((c) => c.name).join(', '),
                  })}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{t('viz.col.date')}</th>
                  {cells.map((cell) => (
                    <th scope="col" key={cell.key}>
                      {cell.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dates.map((date) => (
                  <tr key={date}>
                    <td className="tnum">{dateNumeric(date)}</td>
                    {cells.map((cell) => {
                      const point = cell.points.find((p) => p.date === date);
                      return (
                        <td className="tnum num" key={cell.key}>
                          {point && point.value !== null
                            ? `${decimal(point.value, 1)}${unit}`
                            : t('not-measured')}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      ) : null}
    </Figure>
  );
}

/* ─────────────────────── матрица «вопрос × движок» ────────────────────────── */

/** Четыре состояния клетки. Три их не выражают: уверенность — это согласие двух прогонов. */
export type CellState = 'both' | 'one' | 'none' | 'na';

function cellStateWord(state: CellState): string {
  if (state === 'na') return t('not-measured.inline');
  return t(`viz.cell.${state}`);
}

/**
 * Запасное состояние для столбца таблицы: короткое, потому что название движка
 * уже стоит в шапке, а число прогонов — в подписи.
 *
 * Берётся только тогда, когда клетка своего слова не принесла. Слово клетки
 * длиннее и точнее — оно знает, сколько прогонов УДАЛОСЬ, — и в таблице стоит
 * оно: текстовое подобие обязано говорить то же, что подпись клетки, иначе это
 * второй источник правды, а не подобие.
 */
function cellStateShort(state: CellState): string {
  if (state === 'na') return t('not-measured.inline');
  return t(`viz.cell-short.${state}`);
}

export interface MatrixRow {
  key: string;
  label: string;
  cells: {
    key: string;
    state: CellState;
    /**
     * Состояние словами, если правда длиннее состояния. Клетка знает четыре
     * состояния, а замер — сколько прогонов в ней УДАЛОСЬ: «назван в обоих
     * прогонах» при одном удавшемся прогоне это враньё, и поправить его может
     * только тот, кто считал клетку.
     */
    word?: string;
  }[];
}

/**
 * Матрица вопросов и движков.
 *
 * Клетка — кнопка с полной подписью «вопрос · движок · результат»: смотреть на
 * цвет квадратика ради смысла нельзя, а по сетке нужно ходить стрелками. Tab
 * заводит в сетку один раз, дальше двигают стрелки (roving tabindex).
 */
export function Matrix({
  columns,
  rows,
  caption,
  when,
  onPick,
}: {
  columns: { key: string; name: string }[];
  rows: MatrixRow[];
  caption: ReactNode;
  /** Дата замера в подпись клетки: у матрицы один замер на всю сетку. */
  when?: string;
  onPick?: (rowKey: string, colKey: string) => void;
}) {
  const [cursor, setCursor] = useState({ r: 0, c: 0 });
  const box = useRef<HTMLDivElement>(null);
  // Название движка по ключу колонки: подпись клетки собирается и в сетке, и в
  // таблице под ней, и обе обязаны называть один и тот же движок.
  const names = new Map(columns.map((column) => [column.key, column.name]));

  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const keys = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    setCursor((now) => {
      const r =
        event.key === 'ArrowDown'
          ? Math.min(rows.length - 1, now.r + 1)
          : event.key === 'ArrowUp'
            ? Math.max(0, now.r - 1)
            : now.r;
      const c =
        event.key === 'ArrowRight'
          ? Math.min(columns.length - 1, now.c + 1)
          : event.key === 'ArrowLeft'
            ? Math.max(0, now.c - 1)
            : now.c;
      window.requestAnimationFrame(() => {
        box.current?.querySelector<HTMLButtonElement>(`[data-cell="${r}-${c}"]`)?.focus();
      });
      return { r, c };
    });
  };

  return (
    <Figure caption={caption}>
      <div className="matrix-wrap">
        <div className="matrix-cols">
          <span />
          {columns.map((column) => (
            <span className="matrix-col" key={column.key}>
              {column.name}
            </span>
          ))}
        </div>

        <div className="matrix" ref={box} onKeyDown={move} role="grid" tabIndex={-1}>
          {rows.map((row, r) => (
            <div className="matrix-row" role="row" key={row.key}>
              <span className="matrix-label" role="rowheader">
                {row.label}
              </span>
              {row.cells.map((cell, c) => (
                <span role="gridcell" key={cell.key}>
                  <button
                    type="button"
                    data-cell={`${r}-${c}`}
                    tabIndex={cursor.r === r && cursor.c === c ? 0 : -1}
                    className={`cell ${cell.state}`}
                    // Подпись — вопрос, движок, дата, результат (data-viz.md
                    // §6). Дата здесь не украшение: клетка объясняет ОДИН
                    // замер, а экран открывают через неделю после него.
                    aria-label={[
                      row.label,
                      columns[c]?.name ?? cell.key,
                      ...(when ? [when] : []),
                      cell.word ?? cellStateWord(cell.state),
                    ].join(' · ')}
                    aria-haspopup={onPick ? 'dialog' : undefined}
                    onFocus={() => setCursor({ r, c })}
                    onClick={() => onPick?.(row.key, cell.key)}
                  />
                </span>
              ))}
            </div>
          ))}
        </div>

        <p className="matrix-legend">
          {(['both', 'one', 'none', 'na'] as CellState[]).map((state) => (
            <span key={state}>
              <i className={`cell ${state}`} aria-hidden="true" />
              {cellStateWord(state)}
            </span>
          ))}
        </p>

        {/* Текстовое подобие обязательно у каждого графика (data-viz.md §9), и
            у матрицы оно нужнее, чем у полос: цвет клетки — единственное, что
            здесь есть, а сетку 50×8 подписью к каждой клетке не прочитать. Тот
            же порядок строк и столбцов, что и в сетке: таблица читается как
            перепечатка матрицы, а не как второй источник. */}
        <details className="chart-data">
          <summary>{t('viz.show-data')}</summary>
          <div className="tbl-wrap">
            <table className="mini">
              <caption>{caption}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('viz.col.question')}</th>
                  {columns.map((column) => (
                    <th scope="col" key={column.key}>
                      {column.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key}>
                    <th scope="row">{row.label}</th>
                    {row.cells.map((cell) => (
                      <td key={cell.key}>
                        {/* Таблица здесь не только читается: у клетки мишень
                            17×17 (data-viz.md §6), и пальцу её мало. Строка
                            таблицы открывает ту же клетку мишенью во всю
                            ячейку — второй путь, не требующий ни точности, ни
                            клавиатуры. */}
                        {onPick ? (
                          <button
                            type="button"
                            className="open-cell"
                            /* Имя кнопки — вопрос, движок и состояние. Одно
                               состояние без них озвучивается скринридером
                               четырьмя сотнями одинаковых кнопок подряд, и
                               выбрать среди них нужную клетку нечем: заголовки
                               строки и столбца в доступное имя кнопки не
                               входят. У самой матрицы такое имя уже есть. */
                            aria-label={`${row.label} · ${names.get(cell.key) ?? cell.key} · ${
                              cell.word ?? cellStateShort(cell.state)
                            }`}
                            onClick={() => onPick(row.key, cell.key)}
                          >
                            {cell.word ?? cellStateShort(cell.state)}
                          </button>
                        ) : (
                          (cell.word ?? cellStateShort(cell.state))
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      </div>
    </Figure>
  );
}
